/*
 * ALOHA QUEEN SUPERNOVA - Enhanced Pricing
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Sparkles, Zap, Star, User } from 'lucide-react';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';
import CopyrightFooter from '../components/CopyrightFooter';
import DynamicGradientCard from '../components/DynamicGradientCard';

const EnhancedPricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(tier);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier, billing_cycle: billingCycle })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      name: 'FREE',
      icon: User,
      price: { monthly: 0, annual: 0 },
      features: ['Basic AI Chat', 'Limited Access', '10 messages/day', 'Ads Supported'],
      cta: 'Sign Up Free'
    },
    {
      name: 'BASIC',
      icon: Star,
      price: { monthly: 9.99, annual: 99.99 },
      features: ['Ad-Free', '50 messages/day', '10 deployments/month', 'Email Support'],
      cta: 'Upgrade',
      savings: 20
    },
    {
      name: 'STARTER',
      icon: Sparkles,
      price: { monthly: 29.99, annual: 299.99 },
      features: ['AI Avatar Chat', 'Voice Synthesis', '20 deployments/month', 'Priority Support'],
      cta: 'Upgrade',
      popular: true,
      savings: 60
    },
    {
      name: 'PRO',
      icon: Zap,
      price: { monthly: 49.99, annual: 499.99 },
      features: ['Custom Avatars', '100 deployments/month', 'Unlimited messages', 'API Access'],
      cta: 'Upgrade',
      savings: 100
    },
    {
      name: 'ENTERPRISE',
      icon: Crown,
      price: { monthly: 69.99, annual: 699.99 },
      features: ['Unlimited Everything', 'Dedicated Support', 'Custom Training', 'SLA Guarantee'],
      cta: 'Contact Sales',
      savings: 140
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      <RealisticGalaxyBackground />
      
      <div className="relative z-10 container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Choose Your <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">Universe</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">Unlock the power of AI with flexible pricing</p>
          
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                billingCycle === 'monthly' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                billingCycle === 'annual' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'
              }`}
            >
              Annual <span className="ml-2 text-xs bg-green-500 px-2 py-1 rounded">Save 17%</span>
            </button>
          </div>
          <p className="text-sm text-gray-400">✓ Auto-pay · ✓ Cancel anytime · ✓ Money-back guarantee</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
          {plans.map((plan) => {
            const PlanIcon = plan.icon;
            const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
            
            return (
              <DynamicGradientCard key={plan.name} gradient={plan.popular ? 'royal' : 'purple'}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-1 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PlanIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  
                  <div className="mb-4">
                    <div className="text-4xl font-bold text-white">${price}</div>
                    <div className="text-gray-400 text-sm">per {billingCycle === 'monthly' ? 'month' : 'year'}</div>
                    {plan.savings && billingCycle === 'annual' && (
                      <div className="text-green-400 text-xs mt-1">Save ${plan.savings}/year</div>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-400" />
                      <span className="text-sm text-gray-200">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => plan.name !== 'FREE' && handleUpgrade(plan.name)}
                  disabled={loading === plan.name}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  } disabled:opacity-50`}
                >
                  {loading === plan.name ? 'Processing...' : plan.cta}
                </button>
              </DynamicGradientCard>
            );
          })}
        </div>
      </div>

      <CopyrightFooter />
    </div>
  );
};

export default EnhancedPricing;
